import { gql } from '@apollo/client';
import React, { useMemo } from 'react';
import { Clickable } from '@moda/om';
import ExitIcon from '@moda/icons/exit-24';
import { ResponsiveImage } from '../ResponsiveImage';
import { ProductZoomedGalleryVariantFragment } from '../../generated/types/ProductZoomedGalleryVariantFragment';
import { Modal } from '../Modal';
import './ProductZoomedGallery.scss';

export const PRODUCT_ZOOMED_GALLERY_VARIANT_FRAGMENT = gql`
  fragment ProductZoomedGalleryVariantFragment on VariantData {
    id
    primaryImage: primary_image_urls {
      large
    }
    alternateImages: alternate_image_urls {
      large
    }
  }
`;

export const PRODUCT_ZOOMED_GALLERY_FRAGMENT = gql`
  fragment ProductZoomedGalleryFragment on Variant {
    id
    masterVariant: master_variants_data {
      ...ProductZoomedGalleryVariantFragment
    }
    otherVariants: other_variants_data {
      ...ProductZoomedGalleryVariantFragment
    }
  }
  ${PRODUCT_ZOOMED_GALLERY_VARIANT_FRAGMENT}
`;

interface Props {
  onClose(): void;
  activeVariant: ProductZoomedGalleryVariantFragment;
  activeIndex: number;
}

export const ProductZoomedGallery: React.FC<Props> = ({ onClose, activeVariant, activeIndex }) => {
  const images = useMemo(
    () =>
      [activeVariant.primaryImage, ...activeVariant.alternateImages].map(image => ({
        image,
        ref: React.createRef<HTMLButtonElement>()
      })),
    [activeVariant.alternateImages, activeVariant.primaryImage]
  );

  React.useEffect(() => {
    const activeImage = images[activeIndex];
    activeImage.ref.current?.scrollIntoView();
  }, [activeIndex, images, images.length]);

  return (
    <Modal className="ProductZoomedGallery" onClose={onClose}>
      <Clickable className="ProductZoomedGallery__close" onClick={onClose}>
        <ExitIcon />
      </Clickable>

      <div className="ProductZoomedGallery__column">
        {images.map(({ image, ref }) => {
          return (
            image &&
            image.large && (
              <Clickable
                key={image.large}
                className="ProductZoomedGallery__cell"
                ref={ref}
                onClick={onClose}
              >
                <ResponsiveImage
                  className="ProductZoomedGallery__image"
                  src={image.large}
                  alt="Zoomed product view"
                  maxWidth={800}
                  maxHeight={1600}
                />
              </Clickable>
            )
          );
        })}
      </div>
    </Modal>
  );
};
